import { LitActionResource } from '@lit-protocol/auth-helpers'
import { LIT_ABILITY } from '@lit-protocol/constants'
import { nacl } from '@lit-protocol/nacl'
import type {
  AuthSig,
  SessionKeyPair,
  SessionSigningTemplate,
  SessionSigs,
} from '@lit-protocol/types'
import { base16, utf8 } from '@scure/base'
import { useCallback } from 'react'
import { useLitClient } from './use-lit-client'

/**
 * Hook to get PKP session signatures scoped to specific IPFS action hashes.
 * This creates session sigs with permissions limited to specific Lit Actions,
 * providing better security by restricting what actions can be executed.
 *
 * @param ipfsHashes - Array of IPFS hashes (CIDs) of Lit Actions that should be allowed
 */
export function usePkpScopedSessionSigs() {
  const { litClient, connect: connectLitClient } = useLitClient()
  const getPkpScopedSessionSigs = useCallback(
    async (options: {
      ipfsHashes: string[]
      expiration?: string
    }): Promise<{
      sessionSigs: SessionSigs
      sessionKeyPair: SessionKeyPair
      pkpPublicKey: string
      pkpAddress: string
    }> => {
      await connectLitClient()

      const { ipfsHashes } = options

      if (!ipfsHashes || ipfsHashes.length === 0) {
        throw new Error(
          'At least one IPFS hash is required for scoped session sigs',
        )
      }

      // Read session data from localStorage
      const walletSigStr = window.localStorage.getItem('lit-wallet-sig')
      const sessionKeyStr = window.localStorage.getItem('lit-session-key')
      const expiration = window.localStorage.getItem('lit-session-expiration')
      const tokenIdStr = window.localStorage.getItem('mpc-token-id')

      if (!walletSigStr || !sessionKeyStr || !tokenIdStr) {
        throw new Error(
          'Session data not found in localStorage. Please ensure you are authenticated.',
        )
      }

      // Parse JSON data from localStorage
      const walletSig = JSON.parse(walletSigStr) as AuthSig
      const sessionKeyPair = JSON.parse(sessionKeyStr) as SessionKeyPair

      // tokenId is stored as a plain string, but might be JSON-stringified
      // Parse it if it's JSON, otherwise use as-is
      let tokenId: string
      try {
        const parsed = JSON.parse(tokenIdStr)
        tokenId = typeof parsed === 'string' ? parsed : String(parsed)
      } catch {
        // If not JSON, it's already a plain string
        tokenId = tokenIdStr.trim()
      }

      // Parse expiration if it's JSON-stringified, otherwise use as-is
      let expirationDate: string | null = null
      if (expiration) {
        try {
          const parsed = JSON.parse(expiration)
          expirationDate = typeof parsed === 'string' ? parsed : expiration
        } catch {
          expirationDate = expiration
        }
      }

      // Check if session is expired
      if (expirationDate && new Date(expirationDate).getTime() <= Date.now()) {
        throw new Error('Session has expired. Please re-authenticate.')
      }

      // Extract PKP address from walletSig (similar to lit-server approach)
      const { ethers } = await import('ethers')
      let pkpAddress: string = (walletSig as { address?: string }).address || ''

      // If not in address field, try parsing signedMessage (for session sigs)
      if (!pkpAddress || !ethers.utils.isAddress(pkpAddress)) {
        try {
          const signedMessage = JSON.parse(walletSig.signedMessage)
          // The capabilities array contains the LIT_BLS signature with the PKP address
          const litBlsCapability = signedMessage.capabilities?.find(
            (cap: { algo?: string }) => cap.algo === 'LIT_BLS',
          )
          if (litBlsCapability?.address) {
            pkpAddress = litBlsCapability.address
          }
        } catch {
          // Ignore parsing errors and continue. Error handling is done later.
        }
      }

      if (!pkpAddress || !ethers.utils.isAddress(pkpAddress)) {
        throw new Error(
          'Could not extract valid PKP address from walletSig. Please ensure you are authenticated.',
        )
      }

      // Set session expiration to the expiration date from localStorage, or 1 hour from now if not set
      const sessionExpiration =
        options?.expiration ||
        expirationDate ||
        new Date(Date.now() + 1000 * 60 * 60).toISOString() // 1 hour default

      // Define resources and abilities for scoped session sigs
      // Based on the test file pattern: need both specific IPFS hash and empty string for :currentActionIpfsId verification
      const resources = [
        // Add LitActionExecution permission for each IPFS hash
        ...ipfsHashes.map((ipfsHash) => ({
          resource: new LitActionResource(ipfsHash),
          ability: LIT_ABILITY.LitActionExecution,
        })),
        // Required for Lit's internal action context verification during decryption
        // This allows :currentActionIpfsId to be verified
        {
          resource: new LitActionResource(''),
          ability: LIT_ABILITY.LitActionExecution,
        },
      ]

      // Check if wallet signature is still valid
      const sessionKeyUri = litClient.getSessionKeyUri(sessionKeyPair.publicKey)
      const needToResignSessionKey =
        await litClient.checkNeedToResignSessionKey({
          authSig: walletSig,
          sessionKeyUri,
          resourceAbilityRequests: resources,
        })

      if (needToResignSessionKey) {
        console.warn(
          'Wallet signature may be expired or invalid. Continuing with session signature refresh.',
        )
      }

      // Manually create session sigs for each connected node (same as refreshPkpSessionSigs in lit-server)
      const now = Date.now()
      const signatures: SessionSigs = {}

      // The capabilities is just a slice of auth sig that proves ownership of the PKP
      const capabilities = [walletSig]

      // The signing template is like a form submitting to a Lit node
      const signingTemplate = {
        sessionKey: sessionKeyPair.publicKey,
        resourceAbilityRequests: resources,
        capabilities,
        issuedAt: new Date(now).toISOString(),
        expiration: sessionExpiration,
      }

      // Loop over all connected nodes and create a unique signed message for each
      for (const nodeAddress of litClient.connectedNodes) {
        const toSign: SessionSigningTemplate = {
          ...signingTemplate,
          nodeAddress,
        }

        const signedMessage = JSON.stringify(toSign)

        // Decode the secret key from hex
        const uint8arrayKey = base16.decode(
          sessionKeyPair.secretKey.toUpperCase(),
        )

        // Decode the message to sign
        const uint8arrayMessage = utf8.decode(signedMessage)

        // Sign the message using nacl
        const signature = nacl.sign.detached(uint8arrayMessage, uint8arrayKey)

        signatures[nodeAddress] = {
          sig: base16.encode(signature),
          derivedVia: 'litSessionSignViaNacl',
          signedMessage: signedMessage,
          address: sessionKeyPair.publicKey,
          algo: 'ed25519',
        }
      }

      // Get the PKP public key from the contract
      let pkpPublicKey: string = ''
      try {
        const { LitContracts } = await import('@lit-protocol/contracts-sdk')

        const litContracts = new LitContracts()
        await litContracts.connect()
        const tokenIdBigNumber = ethers.BigNumber.from(tokenId)
        const pkpInfo =
          await litContracts.pkpNftContract.read.getPubkey(tokenIdBigNumber)

        if (
          pkpInfo &&
          pkpInfo !== '0x' &&
          (typeof pkpInfo === 'string' ? pkpInfo.length > 0 : true)
        ) {
          if (typeof pkpInfo === 'string') {
            pkpPublicKey = pkpInfo.startsWith('0x') ? pkpInfo : `0x${pkpInfo}`
          } else {
            pkpPublicKey = ethers.utils.hexlify(
              pkpInfo as unknown as string | number[] | Uint8Array,
            )
          }
        }
      } catch (error) {
        console.warn('Could not get PKP public key from contract:', error)
      }

      const sessionSigs = signatures

      return {
        sessionSigs,
        sessionKeyPair,
        pkpPublicKey,
        pkpAddress,
      }
    },
    [connectLitClient, litClient],
  )

  return {
    getPkpScopedSessionSigs,
  }
}
